<template>
  <p v-if="productRoutes.length > 0" class="my-3 px-5 text-body-s text-subtlest">{{ t('SideBar.reviews.softwareAndTools') }}</p>
  <SideBarSideMenu
    v-for="productRoute in productRoutes"
    :key="productRoute.label"
    :data-testid="'sidebar-reviews-products-' + productRoute.routeParams.productId"
    :label="productRoute.label"
    :to="{ name: productRoute.routeName, params: productRoute.routeParams }"
    :side-bar-urls="[getSidebarUrl({ name: productRoute.routeName, params: productRoute.routeParams })]"
  >
    <template #prependLabel>
      <img v-if="productRoute.logoUrl" :src="productRoute.logoUrl" :alt="`${productRoute.label} logo`" class="mr-2 h-4 w-4" />
      <DockIcon v-else class="mr-2 lucide-icon-s min-w-4" />
    </template>
    <template #prependDropDownLabel>
      <img v-if="productRoute.logoUrl" :src="productRoute.logoUrl" :alt="`${productRoute.label} logo`" class="h-8 w-8" />
    </template>
    <SideBarItem
      :to="{ name: ReviewsRouteName.ReviewsProductViewDetails, params: productRoute.routeParams }"
      data-testid="sidebar-reviews-product-profile"
    >
      {{ t('SideBar.reviews.product.profile') }}
    </SideBarItem>
    <SideBarItem
      :to="{ name: ReviewsRouteName.ReviewsProductReviewsAndCampaigns, params: productRoute.routeParams }"
      :active-routes="[ReviewsRouteName.ReviewsProductCampaignOverview]"
      data-testid="sidebar-reviews-campaigns"
    >
      {{ t('SideBar.reviews.product.reviewsAndCampaigns') }}
    </SideBarItem>
    <SideBarItem
      v-if="isProductLeadsEnabled && canViewProductLeads"
      :to="{ name: ReviewsRouteName.ReviewsProductLeads, params: productRoute.routeParams }"
      data-testid="sidebar-reviews-product-leads"
    >
      {{ t('SideBar.reviews.product.leads') }}
    </SideBarItem>
    <SideBarItem
      v-if="isProductTopProductsRankingEnabled"
      :to="{ name: ReviewsRouteName.ReviewsProductTopProductsRanking, params: productRoute.routeParams }"
      data-testid="sidebar-reviews-product-top-products-ranking"
    >
      {{ t('SideBar.reviews.product.top100Ranking') }}
    </SideBarItem>
    <SideBarItem
      v-if="isProductInsightsEnabled"
      :to="{ name: ReviewsRouteName.ReviewsProductInsights, params: productRoute.routeParams }"
      :active-routes="[
        ReviewsRouteName.ReviewsProductInsightsMarket,
        ReviewsRouteName.ReviewsProductInsightsCompetition,
        ReviewsRouteName.ReviewsProductInsightsBuyer,
        ReviewsRouteName.ReviewsProductInsightsReviews,
      ]"
      data-testid="sidebar-reviews-product-insights"
    >
      {{ t('SideBar.reviews.product.insights') }}
    </SideBarItem>
    <SideBarItem
      :to="{ name: ReviewsRouteName.ReviewsProductResourcesWidgetsView, params: productRoute.routeParams }"
      :active-routes="[ReviewsRouteName.ReviewsProductResourcesView, ReviewsRouteName.ReviewsProductResourcesBadgesView]"
      data-testid="sidebar-reviews-product-resources"
    >
      {{ t('SideBar.reviews.product.resources') }}
    </SideBarItem>
    <SideBarItem
      :to="{ name: ReviewsRouteName.ReviewsProductPreferencesView, params: productRoute.routeParams }"
      data-testid="sidebar-reviews-product-preferences"
    >
      {{ t('SideBar.reviews.product.preferences') }}
    </SideBarItem>
  </SideBarSideMenu>
  <p v-if="agencyRoutes.length > 0" class="my-3 px-5 text-body-s text-subtlest">{{ t('SideBar.reviews.agenciesAndServices') }}</p>
  <SideBarSideMenu
    v-for="agencyRoute in agencyRoutes"
    :key="agencyRoute.label"
    :data-testid="'sidebar-reviews-agencies-' + agencyRoute.routeParams.agencyId"
    :label="agencyRoute.label"
    :to="{ name: agencyRoute.routeName, params: agencyRoute.routeParams }"
    :side-bar-urls="[getSidebarUrl({ name: agencyRoute.routeName, params: agencyRoute.routeParams })]"
  >
    <template #prependLabel>
      <img v-if="agencyRoute.logoUrl" :src="agencyRoute.logoUrl" :alt="`${agencyRoute.label} logo`" class="mr-2 h-4 w-4" />
      <DockIcon v-else class="mr-2 lucide-icon-s min-w-4" />
    </template>
    <template #prependDropDownLabel>
      <img v-if="agencyRoute.logoUrl" :src="agencyRoute.logoUrl" :alt="`${agencyRoute.label} logo`" class="h-8 w-8" />
    </template>
    <SideBarItem
      :to="{ name: ReviewsRouteName.ReviewsAgencyViewDetails, params: agencyRoute.routeParams }"
      data-testid="sidebar-reviews-agency-profile"
    >
      {{ t('SideBar.reviews.agency.profile') }}
    </SideBarItem>
    <SideBarItem
      v-if="isAgencyReviewsEnabled"
      :to="{ name: ReviewsRouteName.ReviewsAgencyReviewsView, params: agencyRoute.routeParams }"
      data-testid="sidebar-reviews-agency-reviews"
    >
      {{ t('SideBar.reviews.agency.reviewsAndCampaigns') }}
    </SideBarItem>
    <SideBarItem
      v-if="isAgencyLeadsEnabled && canViewAgencyLeads"
      :to="{ name: ReviewsRouteName.ReviewsAgencyLeadsView, params: agencyRoute.routeParams }"
      data-testid="sidebar-reviews-agency-leads"
    >
      {{ t('SideBar.reviews.agency.leads') }}
    </SideBarItem>
    <SideBarItem
      :to="{ name: ReviewsRouteName.ReviewsAgencyResourcesWidgetsView, params: agencyRoute.routeParams }"
      :active-routes="[ReviewsRouteName.ReviewsAgencyResourcesView, ReviewsRouteName.ReviewsAgencyResourcesBadgesView]"
      data-testid="sidebar-reviews-agency-resources"
    >
      {{ t('SideBar.reviews.agency.resources') }}
    </SideBarItem>
    <SideBarItem
      :to="{ name: ReviewsRouteName.ReviewsAgencyPreferencesView, params: agencyRoute.routeParams }"
      data-testid="sidebar-reviews-agency-preferences"
    >
      {{ t('SideBar.reviews.agency.preferences') }}
    </SideBarItem>
  </SideBarSideMenu>

  <SideBarItem
    v-if="isOmrViewerEnabled || hasProductsOrAgencies"
    data-testid="sidebar-reviews-omr-viewer"
    :to="isOmrViewerEnabled ? buyerIntentDataUrl : undefined"
    class="w-full text-left hover:cursor-pointer"
    @click="handleOmrViewerClick"
  >
    <div class="flex items-center">
      <span class="pr-1">{{ t('SideBar.reviews.buyerIntentData') }} </span>
      <ExternalLinkIcon class="lucide-icon-s shrink-0" />
    </div>
    <ChipBadge v-if="!isOmrViewerEnabled" rounded color="white" type="outlined" class="mt-2">
      <LockIcon class="mr-1.5 lucide-icon-s shrink-0" />
      <span class="text-body-s-bold">{{ t('SideBar.reviews.omrViewerUpgradeHint') }}</span>
    </ChipBadge>
  </SideBarItem>

  <SideBarItem
    v-if="showAiVisibilityDashboardEntry"
    data-testid="sidebar-reviews-ai-visibility-dashboard"
    :to="aiVisibilityDashboardLink"
    class="w-full text-left hover:cursor-pointer"
    @click="handleAiVisibilityDashboardClick"
  >
    <div class="flex items-center gap-x-1">
      <span class="min-w-0 truncate">{{ t('SideBar.reviews.product.aiVisibilityDashboard') }}</span>
      <ExternalLinkIcon class="lucide-icon-s shrink-0" />
    </div>
    <ChipBadge v-if="!isAiVisibilityDashboardEnabled" rounded color="white" type="outlined" class="mt-2">
      <LockIcon class="mr-1.5 lucide-icon-s shrink-0" />
      <span class="text-body-s-bold">{{ t('SideBar.reviews.product.aiVisibilityDashboardUpgradeHint') }}</span>
    </ChipBadge>
  </SideBarItem>

  <OutlinkFencingModal
    ref="upgradeModalRef"
    :headline="t('Reviews.edit.upgradeModal.title')"
    :body="t('Reviews.edit.upgradeModal.description')"
    :fencing-image-link="omrViewerFencingImg"
    :cta-primary-label="t('Reviews.edit.upgradeModal.moreInfo')"
    :cta-primary-link="t('Reviews.edit.upgradeModal.moreInfoLink')"
    :cta-secondary-label="t('Reviews.edit.upgradeModal.upgrade')"
    :cta-secondary-link="t('Reviews.edit.upgradeModal.upgradeLink')"
  />

  <OutlinkFencingModal
    ref="aiVisibilityDashboardFencedModalRef"
    :headline="t('Reviews.edit.aiVisibilityDashboardFencedModal.title')"
    :body="t('Reviews.edit.aiVisibilityDashboardFencedModal.description')"
    :cta-primary-label="t('Reviews.edit.aiVisibilityDashboardFencedModal.ctaPrimaryLabel')"
    :cta-primary-link="t('Reviews.edit.aiVisibilityDashboardFencedModal.ctaPrimaryLink')"
    :cta-primary-icon="SparklesIcon"
    @cta-primary-click="track('report_request', { surface: 'nav', provider: 'rankscale', request_type: 'access' })"
  />

  <OutlinkFencingModal
    ref="aiVisibilityDashboardSetupModalRef"
    :headline="t('Reviews.edit.aiVisibilityDashboardSetupModal.title')"
    :body="t('Reviews.edit.aiVisibilityDashboardSetupModal.description')"
    :cta-primary-label="t('Reviews.edit.aiVisibilityDashboardSetupModal.ctaPrimaryLabel')"
    :cta-primary-link="t('Reviews.edit.aiVisibilityDashboardSetupModal.ctaPrimaryLink')"
    :cta-primary-icon="LockOpenIcon"
    @cta-primary-click="track('report_request', { surface: 'nav', provider: 'rankscale', request_type: 'activation' })"
  />
</template>

<script setup lang="ts">
import { ChipBadge } from '@ramp106/omrjs-core-ui'
import { DockIcon, ExternalLinkIcon, LockIcon, LockOpenIcon, SparklesIcon } from '@ramp106/omrjs-core-ui/icons'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { type RouteLocationRaw, useRouter } from 'vue-router'

import SideBarItem from '@/components/SideBar/SideBarItem.vue'
import SideBarSideMenu from '@/components/SideBar/SideBarSideMenu.vue'
import { FEATURE_FLAG } from '@/composables/featureFlagCatalog'
import { useFeatureFlag } from '@/composables/useFeatureFlag'
import { buyerIntentDataUrl } from '@/config/env'
import { useReviewsAgenciesQuery, useReviewsProductsQuery, useReviewsCurrentUserQuery } from '@/gql/reviews'
import { ReviewsRouteName } from '@/router/types'
import { $can } from '@/services/roles'

import omrViewerFencingImg from '../ReviewsCommon/Fencing/omrviewer_fencing.png'
import OutlinkFencingModal from '../ReviewsCommon/Fencing/OutlinkFencingModal.vue'

const { t } = useI18n()
const router = useRouter()
const { result: productsResult } = useReviewsProductsQuery({ clientId: 'reviews' })
const { result: agenciesResult } = useReviewsAgenciesQuery({ clientId: 'reviews' })
const { result: currentUserResult } = useReviewsCurrentUserQuery({ clientId: 'reviews' })
const { enabled: isAgencyReviewsEnabled } = useFeatureFlag(FEATURE_FLAG.REVIEWS_AGENCIES.REVIEWS_RELEASE)
const { enabled: isProductLeadsEnabled } = useFeatureFlag(FEATURE_FLAG.REVIEWS_PRODUCTS.LEADS_RELEASE)
const { enabled: isProductTopProductsRankingEnabled } = useFeatureFlag(FEATURE_FLAG.REVIEWS_PRODUCTS.TOP_PRODUCTS_RANKING_RELEASE)
const { enabled: isProductInsightsEnabled } = useFeatureFlag(FEATURE_FLAG.REVIEWS_PRODUCTS.INSIGHTS_HUB_RELEASE)
const { enabled: isAgencyLeadsEnabled } = useFeatureFlag(FEATURE_FLAG.REVIEWS_AGENCIES.LEADS_RELEASE)

const canViewProductLeads = computed(() => $can('viewReviewsProductLeads', { for: 'User' }))
const canViewAgencyLeads = computed(() => $can('viewReviewsAgencyLeads', { for: 'User' }))

const isOmrViewerEnabled = computed(() => !!currentUserResult.value?.manage?.currentUser?.hasAccessToOmrViewer)

const upgradeModalRef = ref()
const aiVisibilityDashboardFencedModalRef = ref()
const aiVisibilityDashboardSetupModalRef = ref()

const products = computed(() => productsResult.value?.manage?.products ?? [])
const isPaidUser = computed(() => products.value.some((p) => p.hasPaidPlan))
const directAiVisibilityDashboardUrl = computed(
  () => products.value.find((p) => p.config.aiVisibilityDashboardCached === 'yes' && !!p.rankscaleUrl)?.rankscaleUrl ?? null,
)
const isAiVisibilityDashboardEnabled = computed(() => products.value.some((p) => p.config.aiVisibilityDashboardCached === 'yes'))
const showAiVisibilityDashboardEntry = computed(() => isPaidUser.value || isAiVisibilityDashboardEnabled.value)
const aiVisibilityDashboardLink = computed(() =>
  isAiVisibilityDashboardEnabled.value ? (directAiVisibilityDashboardUrl.value ?? undefined) : undefined,
)

const productRoutes = computed(() => {
  return products.value.map((product) => ({
    label: product.title,
    logoUrl: product.logo?.url,
    routeName: ReviewsRouteName.ReviewsProductView,
    routeParams: { productId: product.id },
  }))
})

const agencyRoutes = computed(() => {
  if (agenciesResult.value?.manage?.agencies) {
    return agenciesResult.value.manage.agencies.map((agency) => ({
      label: agency.title,
      logoUrl: agency.logo?.url,
      routeName: ReviewsRouteName.ReviewsAgencyView,
      routeParams: { agencyId: agency.id },
    }))
  }

  return []
})

const hasProductsOrAgencies = computed(() => productRoutes.value.length > 0 || agencyRoutes.value.length > 0)

function getSidebarUrl(itemLocation: RouteLocationRaw) {
  return router.resolve(itemLocation).path
}

const handleOmrViewerClick = (event: Event) => {
  if (!isOmrViewerEnabled.value) {
    event.preventDefault()
    upgradeModalRef.value?.show()
  } else {
    track('clickout', { surface: 'nav', destination: 'omr_viewer', target_url: buyerIntentDataUrl })
  }
}

const handleAiVisibilityDashboardClick = (event: Event) => {
  if (!isAiVisibilityDashboardEnabled.value) {
    event.preventDefault()
    aiVisibilityDashboardFencedModalRef.value?.show()
    return
  }

  if (directAiVisibilityDashboardUrl.value) {
    track('clickout', { surface: 'nav', destination: 'rankscale', target_url: directAiVisibilityDashboardUrl.value ?? undefined })
  } else {
    event.preventDefault()
    aiVisibilityDashboardSetupModalRef.value?.show()
  }
}
</script>
